import { lazy, useEffect, Suspense } from "react";
import { useParams } from "react-router-dom";
import { ErrorYogaMan } from "./../../ErrorYogaMan";
import { loadFederatedModule } from "../../utils/loadFederatedModule";

const PromptManagerPage = lazy(() => loadFederatedModule<typeof import("VIRTUAL_AGENT/PromptManagerPage")>("VIRTUAL_AGENT/PromptManagerPage"));

export const PromptConfigPage = () => {
  const { orgId } = useParams();
  // Check if page is loaded in an iframe
  const isInIframe = window.self !== window.top;

  useEffect(() => {
    if (isInIframe) {
      // Add CSS to hide the elements and remove padding
      const style = document.createElement('style');
      style.id = 'iframe-hide-style';
      style.textContent = `
        #mainmenu { display: none !important; }
        #contentTd2 { display: none !important; }
        footer, #footer, .footer { display: none !important; }
        body { overflow: visible !important; }
        html { overflow: visible !important; }
        .wbContent { padding-top: 0 !important; }
        .agent-prompt-manager-page { padding-top: 0 !important; }
        #root { padding-top: 0 !important; }
        #content { padding-top: 0 !important; }
        .container { padding-top: 0 !important; }
        body > * { padding-top: 0 !important; }
      `;
      document.head.appendChild(style);

      // Also try to remove the elements if they exist
      const removeElements = () => {
        // Remove main menu
        const mainMenu = document.getElementById('mainmenu');
        if (mainMenu) {
          mainMenu.remove();
        }

        // Remove footer - try multiple selectors
        const footer = document.getElementById('contentTd2');
        if (footer) {
          footer.remove();
        }

        // Try alternative footer selectors
        const footerElements = document.querySelectorAll('footer, #footer, .footer, #contentTd2');
        footerElements.forEach((el) => {
          el.remove();
        });

        // Remove padding-top from parent elements
        const agentPromptPage = document.querySelector('.agent-prompt-manager-page');
        if (agentPromptPage && agentPromptPage.parentElement) {
          agentPromptPage.parentElement.style.paddingTop = '0';
        }

        // Also remove padding from common containers
        const root = document.getElementById('root');
        if (root) {
          root.style.paddingTop = '0';
        }

        const content = document.getElementById('content');
        if (content) {
          content.style.paddingTop = '0';
        }

        // Remove padding from .wbContent which has padding-top: 65px
        document.querySelectorAll('.wbContent').forEach((el) => {
          (el as HTMLElement).style.paddingTop = '0';
        });

        document.querySelectorAll('.container').forEach((el) => {
          (el as HTMLElement).style.paddingTop = '0';
        });

        // Return true if main elements found
        return mainMenu !== null || footer !== null;
      };

      // Try immediately
      removeElements();

      // Also try after delays in case elements load later
      const timeouts = [100, 500, 1000, 2000].map(delay =>
        setTimeout(removeElements, delay)
      );

      // Function to send height to parent
      let lastSentHeight = 0;
      let sendCount = 0;
      const sendHeightToParent = (force = false) => {
        // Force reflow to get accurate measurements
        void document.body.offsetHeight;
        void document.documentElement.offsetHeight;

        // Get all possible heights
        const bodyScrollHeight = document.body.scrollHeight;
        const bodyOffsetHeight = document.body.offsetHeight;
        const docScrollHeight = document.documentElement.scrollHeight;
        const docOffsetHeight = document.documentElement.offsetHeight;

        const height = Math.max(
          bodyScrollHeight,
          bodyOffsetHeight,
          docScrollHeight,
          docOffsetHeight,
          600 // minimum height
        );

        sendCount++;

        // Always send if height changed (including decreases) OR forced OR first 30 sends
        if (force || height !== lastSentHeight || sendCount <= 30) {
          window.parent.postMessage({
            type: 'org-prompt-resize-iframe',
            height: height
          }, '*');
          lastSentHeight = height;
        }
      };

      // Expose global function to manually trigger resize
      (window as any).updateIframeHeight = () => {
        sendHeightToParent(true);
        // Send multiple times to ensure it catches
        setTimeout(() => sendHeightToParent(true), 50);
        setTimeout(() => sendHeightToParent(true), 150);
        setTimeout(() => sendHeightToParent(true), 300);
        setTimeout(() => sendHeightToParent(true), 500);
      };

      // Listen for custom events for height updates
      window.addEventListener('resize-iframe-content', () => {
        sendHeightToParent(true);
        setTimeout(() => sendHeightToParent(true), 50);
        setTimeout(() => sendHeightToParent(true), 200);
      });

      // Send initial height after delays
      const resizeTimeouts = [500, 1000, 2000, 3000].map(delay =>
        setTimeout(sendHeightToParent, delay)
      );

      // Set up ResizeObserver to watch for content changes
      const resizeObserver = new ResizeObserver(() => {
        sendHeightToParent();
      });

      // Observe body and root for size changes
      resizeObserver.observe(document.body);
      const rootElement = document.getElementById('root');
      if (rootElement) {
        resizeObserver.observe(rootElement);
      }

      // Set up MutationObserver to watch for DOM changes (including removals)
      const mutationObserver = new MutationObserver((mutations) => {
        // Check if any mutations involve removal
        const hasRemovals = mutations.some(mutation => {
          if (mutation.removedNodes.length > 0) {
            return true;
          }
          return false;
        });

        // Check for attribute changes that might affect size
        const hasSizeChanges = mutations.some(mutation => {
          if (mutation.type === 'attributes') {
            const attr = mutation.attributeName;
            return attr === 'style' || attr === 'class' || attr === 'hidden';
          }
          return false;
        });

        // Debounce the height update
        clearTimeout((window as any).heightUpdateTimeout);

        // If removals or size changes detected, update faster
        if (hasRemovals || hasSizeChanges) {
          // Multiple rapid updates to catch the change
          sendHeightToParent();
          setTimeout(sendHeightToParent, 50);
          setTimeout(sendHeightToParent, 150);
        } else {
          (window as any).heightUpdateTimeout = setTimeout(sendHeightToParent, 100);
        }
      });

      // Observe DOM changes
      mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class', 'hidden']
      });

      // Polling as a fallback - check height every 200ms for very responsive resizing
      const pollingInterval = setInterval(() => {
        sendHeightToParent();
      }, 200);

      // Also use requestAnimationFrame for smooth updates
      let rafId: number;
      let rafCount = 0;
      const rafUpdate = () => {
        sendHeightToParent();
        rafCount++;
        // Run for first 10 seconds, then stop RAF to save resources
        if (rafCount < 50) { // 50 frames at ~60fps = ~0.8 seconds
          rafId = requestAnimationFrame(rafUpdate);
        }
      };
      rafId = requestAnimationFrame(rafUpdate);

      return () => {
        // Cleanup
        const styleEl = document.getElementById('iframe-hide-style');
        if (styleEl) {
          styleEl.remove();
        }
        timeouts.forEach(clearTimeout);
        resizeTimeouts.forEach(clearTimeout);
        resizeObserver.disconnect();
        mutationObserver.disconnect();
        clearInterval(pollingInterval);
        cancelAnimationFrame(rafId);
        clearTimeout((window as any).heightUpdateTimeout);
        delete (window as any).updateIframeHeight;
      };
    }
  }, [isInIframe]);

  if (!isInIframe) {
    return <ErrorYogaMan />
  }

  return (
    <Suspense fallback={<div style={{ padding: '20px', textAlign: 'center' }}>Loading Prompt Manager...</div>}>
      <PromptManagerPage org_id={orgId} />
    </Suspense>
  )
}